import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import type { Recipient, Region, SchoolLevel } from '@/types';

interface FormData {
  name: string;
  email: string;
  subscriptions: {
    region_id: string;
    school_level_id: string;
  }[];
}

export default function Recipients() {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [schoolLevels, setSchoolLevels] = useState<SchoolLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subscriptions: [],
  });

  useEffect(() => {
    fetchRecipients();
    fetchRegions();
    fetchSchoolLevels();
  }, []);

  const fetchRecipients = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_recipients')
        .select(`
          *,
          subscriptions:sms_recipient_region_school_levels(
            region:sms_regions(*),
            school_level:sms_school_levels(*)
          )
        `)
        .order('name');

      if (error) throw error;

      setRecipients(data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRegions = async () => {
    const { data, error } = await supabase
      .from('sms_regions')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching regions:', error);
      return;
    }
    setRegions(data);
  };

  const fetchSchoolLevels = async () => {
    const { data, error } = await supabase
      .from('sms_school_levels')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching school levels:', error);
      return;
    }
    setSchoolLevels(data);
  };

  const handleSubmit = async () => {
    try {
      if (selectedRecipient) {
        // Update recipient basic info
        const { error } = await supabase
          .from('sms_recipients')
          .update({ name: formData.name, email: formData.email })
          .eq('id', selectedRecipient.id);

        if (error) throw error;

        // Delete existing subscriptions
        await supabase
          .from('sms_recipient_region_school_levels')
          .delete()
          .eq('recipient_id', selectedRecipient.id);

        // Insert new subscriptions
        if (formData.subscriptions.length > 0) {
          const { error: subscriptionError } = await supabase
            .from('sms_recipient_region_school_levels')
            .insert(
              formData.subscriptions.map(sub => ({
                recipient_id: selectedRecipient.id,
                region_id: sub.region_id,
                school_level_id: sub.school_level_id,
              }))
            );
          
          if (subscriptionError) throw subscriptionError;
        }

        toast.success('Recipient updated successfully');
      } else {
        // Create recipient
        const { data: newRecipient, error } = await supabase
          .from('sms_recipients')
          .insert({ name: formData.name, email: formData.email })
          .select()
          .single();

        if (error) throw error;

        // Insert subscriptions
        if (formData.subscriptions.length > 0) {
          const { error: subscriptionError } = await supabase
            .from('sms_recipient_region_school_levels')
            .insert(
              formData.subscriptions.map(sub => ({
                recipient_id: newRecipient.id,
                region_id: sub.region_id,
                school_level_id: sub.school_level_id,
              }))
            );
          
          if (subscriptionError) throw subscriptionError;
        }

        toast.success('Recipient created successfully');
      }

      setModalOpen(false);
      fetchRecipients();
    } catch (error) {
      console.error('Error saving recipient:', error);
      toast.error('Failed to save recipient');
    }
  };

  const handleDelete = async () => {
    if (!selectedRecipient) return;

    try {
      const { error } = await supabase
        .from('sms_recipients')
        .delete()
        .eq('id', selectedRecipient.id);

      if (error) throw error;

      toast.success('Recipient deleted successfully');
      setDeleteDialogOpen(false);
      fetchRecipients();
    } catch (error) {
      console.error('Error deleting recipient:', error);
      toast.error('Failed to delete recipient');
    }
  };

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    { header: 'Email', accessorKey: 'email' },
    {
      header: 'Subscriptions',
      accessorKey: 'subscriptions',
      cell: ({ row }: any) => (
        <div className="flex flex-wrap gap-1">
          {row.original.subscriptions?.map((sub: any) => (
            <span
              key={`${sub.region.id}-${sub.school_level.id}`}
              className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
            >
              {sub.region.name} - {sub.school_level.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedRecipient(row.original);
              setFormData({
                name: row.original.name,
                email: row.original.email,
                subscriptions: row.original.subscriptions.map((sub: any) => ({
                  region_id: sub.region.id,
                  school_level_id: sub.school_level.id,
                })),
              });
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedRecipient(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Recipients</h1>
          <p className="text-muted-foreground mt-1">Manage notification recipients</p>
        </div>
        <Button
          onClick={() => {
            setSelectedRecipient(null);
            setFormData({
              name: '',
              email: '',
              subscriptions: [],
            });
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Recipient
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={recipients}
        isLoading={isLoading}
      />

      <Modal
        title={selectedRecipient ? 'Edit Recipient' : 'Add Recipient'}
        open={modalOpen}
        size="lg"
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div className="space-y-4">
            <label className="text-sm font-medium block">Subscriptions</label>
            <div className="space-y-6 max-h-[60vh] overflow-y-auto p-4 border rounded-lg">
              {regions.map((region) => {
                return (
                  <div key={region.id} className="space-y-2">
                    <h4 className="font-medium text-sm">{region.name}</h4>
                    <div className="ml-4 space-y-3">
                      {schoolLevels.map((level) => {
                        const isSubscribed = formData.subscriptions.some(
                          sub => sub.region_id === region.id && sub.school_level_id === level.id
                        );
                        return (
                          <div key={level.id} className="flex items-center space-x-3">
                            <Checkbox
                              id={`${region.id}-${level.id}`}
                              checked={isSubscribed}
                              onCheckedChange={(checked) => {
                                setFormData({
                                  ...formData,
                                  subscriptions: checked
                                    ? [...formData.subscriptions, { region_id: region.id, school_level_id: level.id }]
                                    : formData.subscriptions.filter(
                                        sub => !(sub.region_id === region.id && sub.school_level_id === level.id)
                                      ),
                                });
                              }}
                            />
                            <label
                              htmlFor={`${region.id}-${level.id}`}
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {level.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete Recipient"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this recipient? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}